import React, { useRef } from 'react';
import Image from 'next/legacy/image';

import { useEscapeEvent } from '../../hooks/useEscapeEvent';
import { useRefClickEvent } from '../../hooks/useRefClickEvent';

import { ToggleSwitch } from '../ToggleSwitch';
import { StandardWidget } from '../CtaWidget/StandardWidget';

import fullLogo from '@/images/gl-logo-full.svg';
import closeIcon from '@/images/close-icon.svg';

interface ConsentModalProps {
  optedOut: boolean;
  setOptedOut: (val: boolean) => void;
  toggleOptOutModal: () => void;
  onSave: () => void;
}

export function ConsentModal({
  optedOut,
  setOptedOut,
  toggleOptOutModal,
  onSave
}: ConsentModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEscapeEvent(toggleOptOutModal);
  useRefClickEvent(modalRef, toggleOptOutModal);

  const handleSave = () => {
    onSave();
    toggleOptOutModal();
    // Delay to ensure cookie is saved before reload
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  return (
    <>
      <div className="fixed left-0 top-0 z-[999] h-full w-full bg-black opacity-80" />
      <div
        data-testid="consent-modal"
        ref={modalRef}
        className="fixed left-0 right-0 top-[5%] z-[1000] mx-auto flex h-[500px] items-center justify-center rounded-2xl bg-white p-0 text-[18px] shadow-[0px_2px_10px_-3px_#999] small:h-[575px] small:min-w-[300px] small:max-w-[300px] small:text-[16px] tablet:h-[500px] tablet:min-w-[575px] tablet:max-w-[575px] tablet:text-[18px]"
      >
        <div className="flex h-full w-full flex-col overflow-y-scroll text-[0.812em] leading-normal text-black outline-0">
          <div className="flex-1 py-2">
            <div className="box-border flex h-[68px] items-center justify-between p-[30px]">
              <Image
                src={fullLogo}
                alt="Greenlight logo"
                className="mobile:max-h-[30px] mobile:max-w-[200px]"
              />
              <button
                data-testid="header-close-button"
                onClick={() => toggleOptOutModal()}
                className="bg-transparent z-[1] flex h-3 w-3 cursor-pointer items-center justify-center border-none p-0"
                aria-label="Close"
              >
                <Image src={closeIcon} alt="Close" width={12} height={12} />
              </button>
            </div>
            <div>
              <div className="float-left mb-[10px] mt-[10px] w-full pl-[30px] text-[1.3em] font-medium leading-normal text-black">
                Do Not Sell or Share My Personal Information
              </div>
              <p className="clear-both mb-[25px] w-full pl-[30px] pr-[30px] leading-normal text-black">
                We use cookies and other similar technologies on our Site to
                help us advertise our Service on other websites or services you
                visit. We share information we collect on our Site, such as
                information about the pages you visit and the actions you take
                on our Site, with advertising networks and analytics partners to
                support our interest-based advertising, which may qualify as a
                sale or sharing of personal information, or targeted
                advertising, under applicable law.
                <br />
                <br />
                To opt-out of our use or sharing of your information for
                targeted advertising, please adjust the toggle below.
              </p>
              <div className="box-border flex h-[68px] items-center justify-between p-[30px]">
                <h4 style={{ fontWeight: 500 }}>Opt-out</h4>
                <div>
                  <ToggleSwitch
                    toggleName="consent-modal"
                    checked={optedOut}
                    onChange={() => {
                      setOptedOut(!optedOut);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end pb-5 pr-5">
            <StandardWidget
              buttonText="Save and close"
              buttonVariant="primary"
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </>
  );
}
